// Toast - Toast notification component for temporary messages
// Implements US-4.1: WebSocket Infrastructure and Presence System
// Displays join/leave notifications

import React, { useEffect } from 'react';
import styles from './Toast.module.css';

export type ToastType = 'info' | 'success' | 'warning' | 'error';

interface ToastProps {
  message: string;
  type?: ToastType;
  duration?: number; // milliseconds
  index?: number; // Position in stack (for vertical offset)
  onClose: () => void;
}

/**
 * TOAST notification component
 * Displays temporary message that auto-dismisses after duration
 *
 * @param message - Text to display
 * @param type - Toast style variant (info, success, warning, error)
 * @param duration - Auto-dismiss duration in milliseconds (default: 3000)
 * @param onClose - Callback when toast is dismissed
 */
export function Toast({ message, type = 'info', duration = 3000, index = 0, onClose }: ToastProps): React.JSX.Element {
  // AUTO-DISMISS after duration
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, duration);

    return () => {
      clearTimeout(timer);
    };
  }, [duration, onClose]);

  // CALCULATE vertical offset based on index (stack toasts vertically)
  // Each toast is ~60px tall (padding + content), with 8px gap between
  const bottomOffset = 80 + (index * 68); // 80px base + 68px per toast (60px height + 8px gap)

  return (
    <div
      className={`${styles.toast} ${styles[type]}`}
      style={{ bottom: `${bottomOffset}px` }}
      onClick={onClose}
    >
      <span className={styles.message}>{message}</span>
      <button className={styles.closeButton} onClick={onClose} aria-label="Close">
        ×
      </button>
    </div>
  );
}
